import { useState } from 'react'

import {
  Button,
  Field,
  Form,
  FormActions,
  FormCard,
  FormStack,
  Input,
} from 'ui'
import {
  billingFormOptions,
  formatCurrency,
  optionList,
  validationMessagesForField,
} from 'utils/billing'

const SelectInput = ({ options, placeholder, ...props }) => (
  <Input {...props} type="select">
    {placeholder && <option disabled value="">{placeholder}</option>}
    {optionList(options)
      .filter(option => option.value !== '' || !placeholder)
      .map(option => (
        <option key={`${option.value}-${option.label}`} value={option.value}>
          {option.label}
        </option>
      ))}
  </Input>
)

const fieldErrorProps = messages => messages.length === 0 ? {} : {
  'aria-invalid': true,
  className: 'tw:border-status-danger-border',
}

const fieldErrorText = messages => messages.join(', ')

export const PaymentForm = ({
  account,
  formOptions,
  isSubmitting,
  onCancel,
  onSubmit,
  upgrade,
  upgradeRequired,
  validationErrors,
}) => {
  const options = billingFormOptions(formOptions)
  const stateSelectOptions = options.states.length > 1 ? options.states : null
  const countrySelectOptions = options.countries.length > 1 ? options.countries : null
  const [country, setCountry] = useState(account?.country || 'US')
  const [state, setState] = useState(account?.state || '')
  const fieldMessages = fieldName => validationMessagesForField(validationErrors, fieldName)
  const inputErrorProps = fieldName => fieldErrorProps(fieldMessages(fieldName))
  const errorFor = fieldName => fieldErrorText(fieldMessages(fieldName))
  const amount = formatCurrency(upgrade?.amount, upgrade?.currency)

  return (
    <Form className={upgradeRequired ? undefined : 'tw:mx-auto tw:max-w-[760px]'} onSubmit={onSubmit}>
      <FormStack>
        <FormCard title="Card Details">
          <Field error={errorFor('card_type')} label="Card Type">
            <SelectInput defaultValue="" name="card_type" options={options.cardTypes} placeholder="Select card type" required {...inputErrorProps('card_type')} />
          </Field>
          <Field error={errorFor('card_number')} label="Card Number">
            <Input autoComplete="cc-number" inputMode="numeric" name="card_number" placeholder="0000 0000 0000 0000" required {...inputErrorProps('card_number')} />
          </Field>
          <div className="tw:grid tw:grid-cols-1 tw:gap-x-3 tw:md:grid-cols-3">
            <Field error={errorFor('expiration_month')} label="Expiration Month">
              <SelectInput autoComplete="cc-exp-month" defaultValue="" name="expiration_month" options={options.months} placeholder="Month" required {...inputErrorProps('expiration_month')} />
            </Field>
            <Field error={errorFor('expiration_year')} label="Expiration Year">
              <SelectInput autoComplete="cc-exp-year" defaultValue="" name="expiration_year" options={options.years} placeholder="Year" required {...inputErrorProps('expiration_year')} />
            </Field>
            <Field error={errorFor('cvv')} label="CVV">
              <Input autoComplete="cc-csc" inputMode="numeric" name="cvv" placeholder="000" required type="password" {...inputErrorProps('cvv')} />
            </Field>
          </div>
        </FormCard>

        <FormCard title="Billing Information">
          <Field error={errorFor('company_name')} label="Company Name">
            <Input defaultValue={account?.companyName || ''} name="company_name" required {...inputErrorProps('company_name')} />
          </Field>
          <Field error={errorFor('name')} label="Name on Card">
            <Input autoComplete="cc-name" defaultValue={account?.name || ''} name="name" required {...inputErrorProps('name')} />
          </Field>
          <div className="tw:grid tw:grid-cols-1 tw:gap-x-3 tw:md:grid-cols-3">
            <Field className="tw:md:col-span-2" error={errorFor('address')} label="Street Address">
              <Input autoComplete="billing street-address" defaultValue={account?.address || ''} name="address" required {...inputErrorProps('address')} />
            </Field>
            <Field error={errorFor('address_2')} label="Apartment, etc.">
              <Input autoComplete="billing address-line2" defaultValue={account?.address2 || ''} name="address_2" placeholder="Optional" {...inputErrorProps('address_2')} />
            </Field>
          </div>
          <div className="tw:grid tw:grid-cols-1 tw:gap-x-3 tw:md:grid-cols-3">
            <Field error={errorFor('city')} label="City">
              <Input autoComplete="billing address-level2" defaultValue={account?.city || ''} name="city" {...inputErrorProps('city')} />
            </Field>
            <Field error={errorFor('state')} label="State">
              {country === 'US' && stateSelectOptions ? (
                <SelectInput autoComplete="billing address-level1" name="state" onChange={event => setState(event.target.value)} options={stateSelectOptions} placeholder="Select state" value={state} {...inputErrorProps('state')} />
              ) : (
                <Input autoComplete="billing address-level1" name="state" onChange={event => setState(event.target.value)} value={state} {...inputErrorProps('state')} />
              )}
            </Field>
            <Field error={errorFor('zip')} label="ZIP">
              <Input autoComplete="billing postal-code" defaultValue={account?.zip || ''} name="zip" {...inputErrorProps('zip')} />
            </Field>
          </div>
          <Field error={errorFor('country')} label="Country">
            {countrySelectOptions ? (
              <SelectInput autoComplete="billing country" name="country" onChange={event => setCountry(event.target.value)} options={countrySelectOptions} placeholder="Select country" required value={country} {...inputErrorProps('country')} />
            ) : (
              <Input autoComplete="billing country" maxLength="2" name="country" onChange={event => setCountry(event.target.value)} required value={country} {...inputErrorProps('country')} />
            )}
          </Field>
        </FormCard>

        <FormCard title="Contact Information">
          <div className="tw:grid tw:grid-cols-1 tw:gap-x-3 tw:md:grid-cols-2">
            <Field error={errorFor('phone_number')} label="Phone">
              <Input autoComplete="tel" defaultValue={account?.phoneNumber || ''} name="phone_number" {...inputErrorProps('phone_number')} />
            </Field>
            <Field error={errorFor('email')} label="Billing Email">
              <Input autoComplete="email" defaultValue={account?.email || ''} name="email" required type="email" {...inputErrorProps('email')} />
            </Field>
          </div>
        </FormCard>
      </FormStack>

      <FormActions className={`tw:mt-5 ${upgradeRequired ? 'tw:justify-between' : 'tw:justify-center'}`}>
        <Button disabled={isSubmitting} onClick={onCancel} type="button" variant="ghost">
          {upgradeRequired ? 'Back' : 'Back to Billing'}
        </Button>
        <Button disabled={isSubmitting} type="submit" variant={upgradeRequired ? 'cta' : 'primary'}>
          {isSubmitting
            ? upgradeRequired ? 'Processing payment…' : 'Setting Credit Card…'
            : upgradeRequired ? `Pay ${amount} and download` : 'Set Credit Card'}
        </Button>
      </FormActions>
    </Form>
  )
}

export default PaymentForm
